import { apiClient } from './client';

export type BackupKind = 'MANUAL' | 'DAILY' | 'WEEKLY' | 'PRE_RESTORE';
export type BackupJobStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface DatabaseBackup {
  id: number;
  filename: string;
  kind: BackupKind;
  status: BackupJobStatus;
  databaseName: string;
  sizeBytes: number | null;
  errorMessage: string | null;
  createdByName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  deletedAt: string | null;
  deletedByName: string | null;
  available: boolean;
}

export interface DatabaseRestoreRecord {
  id: number;
  status: BackupJobStatus;
  filename: string;
  preRestoreFilename: string | null;
  initiatedByName: string;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface BackupSettings {
  dailyEnabled: boolean;
  dailyTime: string;
  weeklyEnabled: boolean;
  weeklyDay: number;
  weeklyTime: string;
  dailyRetention: number;
  weeklyRetention: number;
}

export interface BackupToolInfo {
  path: string;
  available: boolean;
  version: string | null;
  flavor: 'mysql' | 'mariadb' | null;
}

export interface BackupOverview {
  databaseName: string;
  lastSuccessful: DatabaseBackup | null;
  latest: DatabaseBackup | null;
  totalAvailable: number;
  totalSizeBytes: number;
  activeJob: { type: 'BACKUP' | 'RESTORE'; id: number; status: BackupJobStatus; filename: string } | null;
  history: DatabaseBackup[];
  restores: DatabaseRestoreRecord[];
  settings: BackupSettings & { schedulerRunning: boolean; timeZone: string };
  tools: { dump: BackupToolInfo; restore: BackupToolInfo; storage: { writable: boolean; freeBytes: number | null } };
}

export const getBackupOverview = async (): Promise<BackupOverview> => {
  const { data } = await apiClient.get('/backups');
  return data.data;
};

export const createBackupNow = async (): Promise<DatabaseBackup> => {
  const { data } = await apiClient.post('/backups');
  return data.data;
};

export const saveBackupSettings = async (settings: BackupSettings): Promise<BackupOverview> => {
  const { data } = await apiClient.put('/backups/settings', settings);
  return data.data;
};

export const deleteBackup = async (id: number, confirmFilename: string) => {
  await apiClient.delete(`/backups/${id}`, { data: { confirmFilename } });
};

export const restoreBackup = async (id: number, body: { confirmFilename: string; password: string; acknowledge: true }) => {
  const { data } = await apiClient.post(`/backups/${id}/restore`, body);
  return data.data as { id: number; status: BackupJobStatus };
};

/** Downloads a backup: a single-use, 60-second ticket from the API, then
 * the browser fetches the file itself and saves it straight to disk (a
 * large backup never has to fit in page memory). */
export const downloadBackup = async (id: number) => {
  const { data } = await apiClient.post(`/backups/${id}/download-token`);
  const base = (apiClient.defaults.baseURL ?? '/api').replace(/\/$/, '');
  const link = document.createElement('a');
  link.href = `${base}/backups/download/${data.data.token}`;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};
